import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowUpRight, BadgeCheck, Ban, CircleCheck, CreditCard, FileCheck2, Gauge, HandHeart, Landmark, MessageCircleWarning, ShieldAlert, Tags, Timer,
} from 'lucide-react';
import { Status, formatNumber } from 'src/stage/StageUi';
import { qualityOf } from 'src/features/Dashboard/homeModel';
import { BILLING_HUB_URL, TIER_STEPS, tierIndex } from './pricingModel';

const QUALITY_LEVELS = [
  { id: 'GREEN', label: 'High', tone: 'live', text: 'Little or no negative feedback. Keep doing what you do.' },
  { id: 'YELLOW', label: 'Medium', tone: 'warning', text: 'Several people blocked or reported you, or few read your messages. Slow down and make messages more useful.' },
  { id: 'RED', label: 'Low', tone: 'failed', text: 'Many negative signals. Templates can be paused or disabled soon. Stop broadcasts and fix what people complain about.' },
];

const RULES = [
  { icon: HandHeart, title: 'Only message people who said yes', text: 'Get clear permission (opt-in) before sending templates, and make it easy to stop. People who didn’t ask are the ones who block and report.' },
  { icon: Tags, title: 'Pick the honest category', text: 'An offer is marketing even if it sits in an order update. Meta checks and can re-categorise a template; you pay the category it has when you send it.' },
  { icon: MessageCircleWarning, title: 'Don’t flood people with marketing', text: 'Meta limits how many marketing messages one person gets from businesses. If someone got too many recently, Meta may not deliver yours; undelivered messages are not charged.' },
  { icon: Timer, title: 'Answer inside 24 hours', text: 'Replies inside the window can be any message and the first 1,000 a month are free. Late answers need a paid template.' },
  { icon: CreditCard, title: 'Keep a payment method saved', text: 'From 1 October 2026 replies beyond the free 1,000 are only delivered if your WhatsApp account has a payment method in Billing Hub.' },
  { icon: Landmark, title: 'India: switch billing to rupees', text: 'If Meta bills your business in India, move every WhatsApp account to INR billing by 31 December 2026. From 1 January 2027 Meta stops delivering messages from accounts that haven’t moved.' },
];

export default function StayHealthy({ usage }) {
  const numbers = usage?.data?.phone_numbers || [];
  const current = numbers.reduce((best, number) => Math.max(best, tierIndex(number.messaging_limit_tier)), -1);
  const verification = usage?.data?.account?.business_verification_status;

  return (
    <div className="mb-healthy">
      <section className="ws-card ws-card-pad">
        <div className="mb-card-head">
          <span className="ws-icon-tile" aria-hidden="true"><Gauge size={17} /></span>
          <div className="ws-grow">
            <h2 className="ws-h3">How many new people you can message a day</h2>
            <p className="ws-muted">Meta limits how many different people your business can reach outside the 24-hour window in any 24 hours. The limit is shared by all your numbers.</p>
          </div>
        </div>
        <ol className="mb-ladder">
          {TIER_STEPS.map((step, index) => (
            <li key={step.id} className={`${index <= current ? 'is-reached' : ''}${index === current ? ' is-current' : ''}`} style={{ animationDelay: `${0.1 + index * 0.12}s` }}>
              <span className="mb-ladder-bar" style={{ '--h': `${30 + index * 17}%` }}><b className="ws-num">{step.label}</b></span>
              <strong>{index === current ? 'You are here' : index < current ? 'Done' : `Level ${index + 1}`}</strong>
              <small>{step.how}</small>
            </li>
          ))}
        </ol>
        <p className="wa-help">
          Replies to people who wrote to you in the last 24 hours don’t count toward this limit.
          {verification ? ` Business verification: ${String(verification).replace(/_/g, ' ').toLowerCase()}.` : ''}
        </p>
      </section>

      <div className="mb-grid">
        <section className="ws-card ws-card-pad">
          <div className="mb-card-head">
            <span className="ws-icon-tile" aria-hidden="true"><BadgeCheck size={17} /></span>
            <div className="ws-grow"><h2 className="ws-h3">Quality rating</h2><p className="ws-muted">Meta rates your number and each template from blocks, reports and how often people read them.</p></div>
          </div>
          <ul className="mb-quality">
            {QUALITY_LEVELS.map((level) => (
              <li key={level.id}><Status tone={level.tone}>{level.label}</Status><span>{level.text}</span></li>
            ))}
          </ul>
          {numbers.length > 0 && (
            <ul className="mb-quality-now">
              {numbers.map((number) => {
                const quality = qualityOf(number.quality_rating);
                return <li key={number.id}><span>{number.display_name || number.phone_number}</span><Status tone={quality.tone}>{quality.label}</Status></li>;
              })}
            </ul>
          )}
        </section>

        <section className="ws-card ws-card-pad">
          <div className="mb-card-head">
            <span className="ws-icon-tile" aria-hidden="true"><FileCheck2 size={17} /></span>
            <div className="ws-grow"><h2 className="ws-h3">When templates get paused</h2><p className="ws-muted">A template that keeps getting negative feedback is first sent slowly, then paused, and can end up disabled.</p></div>
          </div>
          <ol className="mb-steps">
            <li><span>1</span><div><strong>Sent to a few people first</strong><p>New or low-rated templates may go to a small group first while Meta watches the reaction.</p></div></li>
            <li><span>2</span><div><strong>Paused</strong><p>If feedback stays negative, Meta pauses the template for a while. Sends fail until it’s active again.</p></div></li>
            <li><span>3</span><div><strong>Disabled</strong><p>Repeated pauses can disable it. You’ll need a new, better template.</p></div></li>
          </ol>
          <Link className="ws-btn ws-btn--quiet ws-btn--sm" to="/templates">Check your templates</Link>
        </section>
      </div>

      <section className="ws-card ws-card-pad">
        <div className="mb-card-head">
          <span className="ws-icon-tile" aria-hidden="true"><ShieldAlert size={17} /></span>
          <div className="ws-grow"><h2 className="ws-h3">Rules that keep you safe</h2><p className="ws-muted">Follow these and your costs stay predictable, your limits grow and your number stays healthy.</p></div>
        </div>
        <ul className="mb-rules">
          {RULES.map(({ icon: Icon, title, text }, index) => (
            <li key={title} style={{ animationDelay: `${0.05 + index * 0.08}s` }}>
              <span className="mb-rule-icon" aria-hidden="true"><Icon size={17} /></span>
              <div><strong>{title}</strong><p>{text}</p></div>
            </li>
          ))}
        </ul>
      </section>

      <section className="ws-card ws-card-pad mb-dont">
        <div className="mb-card-head">
          <span className="ws-icon-tile is-bad" aria-hidden="true"><Ban size={17} /></span>
          <div className="ws-grow"><h2 className="ws-h3">Things that get numbers blocked</h2></div>
        </div>
        <ul className="mb-dont-list">
          <li>Buying or scraping phone number lists</li>
          <li>Sending the same offer again and again</li>
          <li>Hiding marketing inside “utility” messages</li>
          <li>No way to stop getting messages</li>
          <li>Messaging at night or in bursts of thousands</li>
        </ul>
        <div className="mb-dont-foot">
          <CircleCheck size={15} aria-hidden="true" />
          <span>Your healthiest numbers: {numbers.length ? `${formatNumber(numbers.filter((number) => String(number.quality_rating).toUpperCase() === 'GREEN').length)} of ${formatNumber(numbers.length)} rated high quality.` : 'connect a number to see its rating.'}</span>
          <a className="ws-btn ws-btn--plain ws-btn--sm" href={BILLING_HUB_URL} target="_blank" rel="noreferrer">Billing Hub<ArrowUpRight size={13} /></a>
        </div>
      </section>
    </div>
  );
}
